import {
  BrowserCacheLocation,
  type Configuration,
  type RedirectRequest
} from '@azure/msal-browser';

/**
 * Environment-backed MSAL configuration for Entra Workforce.
 *
 * Platform injects `ENTRA_*`. Browser code also accepts `VITE_ENTRA_*` when present.
 * Redirect URI is always normalized to the project origin root (`/`) so it matches the
 * shared CDF app-registration wildcard (`https://*.dev.cdf…/`).
 */

function readPublicEnv(name: string): string | undefined {
  const meta = import.meta.env as Record<string, string | boolean | undefined>;
  const candidates = [`VITE_${name}`, name];
  for (const key of candidates) {
    const raw = meta[key];
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
}

/** True when Entra Workforce client configuration is present and enabled. */
export function isAuthConfigured(): boolean {
  const explicitlyEnabled = readPublicEnv('ENTRA_ENABLED');
  if (explicitlyEnabled === 'false') return false;

  const clientId = readPublicEnv('ENTRA_CLIENT_ID');
  const authority = readPublicEnv('ENTRA_AUTHORITY');
  if (!clientId || !authority) return false;

  // When ENTRA_ENABLED is set, require the truthy flag. When absent, client+authority are enough.
  if (explicitlyEnabled !== undefined && explicitlyEnabled !== 'true') return false;
  return true;
}

/**
 * Always the project origin root, including trailing `/`.
 * Configured ENTRA_REDIRECT_URI values that point at `/redirect.html` or callbacks are collapsed.
 */
export function getRedirectUri(): string {
  const configured = readPublicEnv('ENTRA_REDIRECT_URI');
  try {
    if (configured) {
      const url = new URL(configured, window.location.origin);
      return `${url.origin}/`;
    }
  } catch {
    // fall through to window origin
  }
  return `${window.location.origin}/`;
}

export function getPostLogoutRedirectUri(): string {
  const configured =
    readPublicEnv('ENTRA_POST_LOGIN_REDIRECT') ?? readPublicEnv('ENTRA_REDIRECT_URI');
  try {
    if (configured) {
      const url = new URL(configured, window.location.origin);
      // Only allow same-origin post-logout targets; always land on a path under `/`.
      if (url.origin === window.location.origin) {
        return `${url.origin}${url.pathname.startsWith('/') ? url.pathname : '/'}` || `${url.origin}/`;
      }
    }
  } catch {
    // fall through
  }
  return getRedirectUri();
}

/** OIDC / Graph scopes for the interactive browser session. */
export function getLoginScopes(): string[] {
  return (readPublicEnv('ENTRA_SCOPES') ?? 'openid profile email offline_access')
    .split(/\s+/)
    .filter(Boolean);
}

/** Delegated custom-API scope(s), separate from sign-in and Graph permissions. */
export function getApiScopes(): string[] | null {
  const raw = readPublicEnv('ENTRA_API_SCOPES');
  if (!raw) return null;
  const scopes = raw.split(/\s+/).filter(Boolean);
  return scopes.length > 0 ? scopes : null;
}

export function buildMsalConfig(): Configuration {
  const clientId = readPublicEnv('ENTRA_CLIENT_ID');
  const authority = readPublicEnv('ENTRA_AUTHORITY');
  if (!clientId || !authority) {
    throw new Error('MSAL requires ENTRA_CLIENT_ID and ENTRA_AUTHORITY.');
  }

  return {
    auth: {
      clientId,
      authority,
      redirectUri: getRedirectUri(),
      postLogoutRedirectUri: getPostLogoutRedirectUri()
    },
    cache: {
      // Shared across same-origin tabs (preview iframe + top-level sign-in tab).
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowRedirectInIframe: false,
      allowPlatformBroker: false
    }
  };
}

export function buildLoginRequest(redirectStartPage?: string): RedirectRequest {
  return {
    scopes: getLoginScopes(),
    redirectUri: getRedirectUri(),
    ...(redirectStartPage ? { redirectStartPage } : {})
  };
}

export function buildTokenRequest(
  account: RedirectRequest['account'],
  redirectStartPage?: string
): RedirectRequest {
  const apiScopes = getApiScopes();
  return {
    account: account ?? undefined,
    scopes: apiScopes ?? [],
    redirectUri: getRedirectUri(),
    ...(redirectStartPage ? { redirectStartPage } : {})
  };
}

/**
 * Delegated Microsoft Graph scopes for Rooms & Resources (Places + free/busy).
 * Must be requested separately from custom-API / ID-token scopes.
 */
export type GraphAccess = 'calendar' | 'places' | 'presence' | 'directory' | 'managerReports' | 'groups';

export const GRAPH_SCOPES: Record<GraphAccess, readonly string[]> = {
  calendar: ['Calendars.ReadBasic'],
  places: ['Place.Read.All'],
  presence: ['Presence.Read.All'],
  directory: ['User.ReadBasic.All'],
  managerReports: ['User.Read', 'User.ReadBasic.All'],
  groups: ['User.Read', 'User.ReadBasic.All', 'GroupMember.Read.All']
};

export function getGraphScopes(access: GraphAccess): string[] {
  if (access === 'managerReports') {
    const scopes = getLoginScopes();
    // Preserve the owner's existing grant. User.Read also permits basic organization identity.
    return scopes.some(scope => scope === 'User.Read.All' || scope === 'https://graph.microsoft.com/User.Read.All')
      ? ['User.Read', 'User.Read.All'] : [...GRAPH_SCOPES.managerReports];
  }
  return [...GRAPH_SCOPES[access]];
}

/** Token request for browser → Graph only (never mixed with API scopes). */
export function buildGraphTokenRequest(
  account: RedirectRequest['account'],
  redirectStartPage?: string,
  access: GraphAccess = 'calendar'
): RedirectRequest {
  return {
    account: account ?? undefined,
    scopes: getGraphScopes(access),
    redirectUri: getRedirectUri(),
    ...(redirectStartPage ? { redirectStartPage } : {})
  };
}
